import {useState, useEffect} from 'react'
import deepEqual from 'fast-deep-equal'
import theme from '@/config/theme'
import logger from '@/lib/logger'
import {onInputFocus} from '@/lib/inputFocus'
import useToggle from '@/lib/hooks/useToggle'
import {DROPDOWN_OPTION_HEIGHT} from '../../constants'
import Dropdown from '@emcasa/ui-dom/components/Dropdown'
import PhoneInput from '@/components/form/PhoneInput'
import EmailInput from '@/components/form/EmailInput'
import Modal from './Modal'
import Terms from './Terms'
import composesStyles from '@/styles/_settings/composes.module.css'
import labelStyles from '@/styles/atoms/Label/styles.module.css'
import inputStyles from '@/styles/atoms/Input/styles.module.css'

const deepEqualStrategy = {
  isSelected: deepEqual,
  update: (_, value) => value
}

const Profile = ({
  state,
  districts,
  onChange,
  onSubmit,
  userProfile,
  getLogStates,
  loggerAction
}) => {
  const [loggerInteraction, setInteraction] = useState({
    name: null,
    phone: null,
    email: null,
    creci: null,
    cpf: null,
    city: null,
    terms: null
  })
  const [isModalVisible, showModal] = useState(false)

  const getDistricts = () => Object.entries(districts)

  function onInputChange(name, value) {
    onChange({[name]: value})
    if (!loggerInteraction[name]) {
      setInteraction({
        ...loggerInteraction,
        [name]: true
      })
      logger.action(`${loggerAction.oninputchange}${name}`, {
        [name]: value,
        ...getLogStates()
      })
    }
  }

  function onNameChange({currentTarget: {value}}) {
    onInputChange('name', value)
  }

  function onPhoneChange(value) {
    onInputChange('phone', value)
  }

  function onEmailChange({currentTarget: {value}}) {
    onInputChange('email', value)
  }

  function onCRECIChange({currentTarget: {value}}) {
    onInputChange('creci', value)
  }

  function onCPFChange({currentTarget: {value}}) {
    onInputChange('cpf', value)
  }

  function onSelectsChange(value) {
    onInputChange('city', value)
  }

  function onTermsCheckboxChange({currentTarget: {checked}}) {
    onInputChange('terms', checked)
  }

  function onTermsClick() {
    showModal(true)
    logger.action(loggerAction.profilemodalopen)
  }

  function handleSubmit(e) {
    e.preventDefault()
    if (onSubmit) {
      onSubmit()
    }
  }

  useEffect(() => {
    logger.action(loggerAction.onload, getLogStates())
  }, [])

  const focus = useToggle()

  const dropDownMarginBottom = {
    '--marginbottom': focus.active
      ? `${((getDistricts() && getDistricts().length) || 1) *
          DROPDOWN_OPTION_HEIGHT}px`
      : `${theme.space[5]}px`
  }

  return (
    <>
      <form
        onSubmit={handleSubmit}
        className={`${composesStyles.animation_enterfrombottom} ${composesStyles.paddingx} ${composesStyles.fullmaxwidth}`}
      >
        {state && (
          <>
            <p className={labelStyles.ecLabel_formItem}>
              Precisamos de algumas informações de contato para criar o seu{' '}
              <strong>cadastro de Corretor Parceiro</strong>
            </p>
            <div
              className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
            >
              <span className={labelStyles.ecLabel__value}>Nome</span>
              <input
                name="name"
                type="text"
                tabIndex="0"
                className={inputStyles.ecInput}
                value={state.name}
                onChange={onNameChange}
                onFocus={(e) => onInputFocus(e.target.offsetTop)}
              />
            </div>
            <div
              className={`${labelStyles.ecLabel} ${labelStyles.ecLabel_formItem}`}
            >
              <span className={labelStyles.ecLabel__value}>Telefone </span>
              <PhoneInput
                name="phone"
                type="tel"
                tabIndex="0"
                readOnly={userProfile && userProfile.phone ? true : false}
                value={state.phone || undefined}
                onChange={onPhoneChange}
                onFocus={(e) => onInputFocus(e.target.offsetTop)}
              />
            </div>
            <div className={labelStyles.ecLabel}>
              <span className={labelStyles.ecLabel__value}>E-mail</span>
              <EmailInput
                name="email"
                type="email"
                tabIndex="0"
                placeholder="Ex: nome@email.com"
                value={state.email || undefined}
                onChange={onEmailChange}
                onFocus={(e) => onInputFocus(e.target.offsetTop)}
              />
            </div>
            <div className={labelStyles.ecLabel}>
              <span className={labelStyles.ecLabel__value}>
                CPF
                <span className={labelStyles.ecLabel__value__info}>
                  (necessário para realizar seus pagamentos)*
                </span>
              </span>
              <input
                name="cpf"
                type="tel"
                tabIndex="0"
                arial-label="Seu CPF"
                placeholder="Ex: 123.456.789-10"
                className={inputStyles.ecInput}
                value={state.cpf || undefined}
                onChange={onCPFChange}
                onFocus={(e) => onInputFocus(e.target.offsetTop)}
              />
            </div>
            <div className={labelStyles.ecLabel}>
              <span className={labelStyles.ecLabel__value}>CRECI*</span>
              <input
                name="creci"
                type="text"
                tabIndex="0"
                arial-label="Seu CRECI"
                placeholder="Ex: J-7712"
                className={inputStyles.ecInput}
                value={state.creci || undefined}
                onChange={onCRECIChange}
                onFocus={(e) => onInputFocus(e.target.offsetTop)}
              />
            </div>
            {districts && (
              <div className={labelStyles.ecLabel} style={dropDownMarginBottom}>
                <span className={labelStyles.ecLabel__value}>
                  Área de atuação*
                </span>
                <Dropdown
                  selectedValue={state.city}
                  strategy={deepEqualStrategy}
                  placeholder="Selecione a cidade"
                  flexDirection="row-reverse"
                  icon={focus.active ? 'chevron-up' : 'chevron-down'}
                  height={40}
                  onChange={onSelectsChange}
                  onFocus={() => {
                    if (focus && !focus.active) {
                      focus.on()
                    }
                  }}
                  onBlur={focus.off}
                  borderColor="red500"
                >
                  {getDistricts().map((item, index) => (
                    <Dropdown.Option key={index} value={item[1].citySlug}>
                      {item[1].city}
                    </Dropdown.Option>
                  ))}
                </Dropdown>
              </div>
            )}
            <Terms
              state={state}
              onTermsClick={onTermsClick}
              onCheckboxChange={onTermsCheckboxChange}
            />
          </>
        )}
        <button className={composesStyles.formhiddenbutton} type="submit">
          enviar
        </button>
      </form>
      <Modal
        show={isModalVisible}
        loggerAction={loggerAction}
        onClose={() => showModal(false)}
      />
    </>
  )
}

export default Profile
